/**
 * Implementação de PushProvider usando Firebase Cloud Messaging Web.
 *
 * Este provider implementa a interface PushProvider de @meiuca/push,
 * permitindo que o PushNotificationsService use o Firebase como backend
 * de push notifications.
 *
 * Dependency Inversion: Firebase depende de @meiuca/push (interface), não
 * o contrário — mesmo padrão dos providers de Analytics e Remote Config.
 */

import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from 'firebase/messaging';

import { getFirebaseApp, isFirebaseEnabled } from './firebase';
import type {
  PushAuthorizationStatus,
  PushMessage,
  PushProvider,
  PushToken,
} from '@meiuca/push';

export interface FirebaseMessagingOptions {
  /**
   * VAPID public key — `VITE_FIREBASE_VAPID_KEY` no .env do app.
   * Generated at Firebase Console > Project Settings > Cloud Messaging >
   * Web configuration > Web push certificates.
   */
  vapidKey: string;

  /**
   * Service Worker registration to associate with messaging. If omitted,
   * Firebase Web SDK auto-registers `/firebase-messaging-sw.js` from the
   * site root.
   */
  serviceWorkerRegistration?: ServiceWorkerRegistration;
}

/**
 * Provider de push notifications usando Firebase Cloud Messaging.
 */
export class FirebaseMessagingProvider implements PushProvider {
  private messaging: Messaging | null = null;
  private initialized = false;
  private supported = false;

  constructor(private readonly options: FirebaseMessagingOptions) {}

  async configure(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    if (!isFirebaseEnabled()) {
      console.warn('[FIREBASE_MESSAGING] Firebase app not initialized — call initializeFirebaseApp first');
      return;
    }

    try {
      this.supported = await isSupported();
      if (!this.supported) {
        console.warn('[FIREBASE_MESSAGING] Browser does not support Firebase Messaging');
        return;
      }

      const app = getFirebaseApp();
      if (!app) {
        throw new Error('Firebase app not initialized');
      }

      this.messaging = getMessaging(app);
    } catch (error) {
      console.error('[FIREBASE_MESSAGING] Failed to initialize:', error);
    }
  }

  async requestAuthorization(): Promise<PushAuthorizationStatus> {
    if (typeof Notification === 'undefined') return 'denied';

    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';

    const result = await Notification.requestPermission();
    if (result === 'granted') return 'granted';
    if (result === 'denied') return 'denied';
    return 'not-determined';
  }

  async currentToken(): Promise<PushToken | null> {
    if (!this.messaging) return null;

    try {
      const value = await getToken(this.messaging, {
        vapidKey: this.options.vapidKey,
        serviceWorkerRegistration: this.options.serviceWorkerRegistration,
      });

      if (!value) return null;

      return { value, providerKind: 'firebase' };
    } catch (error) {
      console.error('[FIREBASE_MESSAGING] getToken failed:', error);
      return null;
    }
  }

  /**
   * Firebase Web SDK does not expose a native token-refresh callback. This
   * is a no-op — callers should re-run `currentToken()` on relevant
   * lifecycle events (re-login, app focus) to pick up rotation.
   *
   * Documented in @meiuca/push README.
   */
  onTokenUpdate(_handler: (token: PushToken) => void): () => void {
    return () => {
      // no-op
    };
  }

  /**
   * Com a aba em foco o FCM **não exibe nada** — o service worker só cuida do
   * caso background. Ele entrega o payload aqui e cabe ao app decidir. Sem
   * este caminho a notificação chega e some, enquanto o envio reporta sucesso.
   */
  onForegroundMessage(handler: (message: PushMessage) => void): () => void {
    if (!this.messaging) {
      return () => {
        // Provider não configurado — nada a observar.
      };
    }

    return onMessage(this.messaging, (payload) => {
      handler({
        title: payload.notification?.title,
        body: payload.notification?.body,
        link: payload.fcmOptions?.link,
        data: payload.data ?? {},
      });
    });
  }
}
